/**
 * OddsComparisonWidget — Full multi-sport odds comparison (THE BIG ONE)
 * Extracted from: fetchOdds() lines 2097-2182, plus region/market/bookmaker
 *   selectors, token cost calculator, and advanced options
 * API: GET /odds/:sport (regions x markets tokens)
 * CSS prefix: ocw-
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { fetchOddsSports, fetchOddsForSport } from '../lib/api'
import { getSportIcon, US_BOOKMAKERS, UK_BOOKMAKERS, EU_BOOKMAKERS, AU_BOOKMAKERS } from '../lib/oddsConstants'
import { formatBookmakerName } from '../lib/oddsFormatters'
import OddsTable from './shared/OddsTable'
import OddsChart from './shared/OddsChart'
import { BarChart3, RefreshCw, ChevronRight, ChevronDown, Settings, AlertTriangle } from 'lucide-react'
import './OddsComparisonWidget.css'

const REGIONS = [
  { id: 'us', label: 'US' },
  { id: 'us2', label: 'US2' },
  { id: 'uk', label: 'UK' },
  { id: 'eu', label: 'EU' },
  { id: 'au', label: 'AU' },
]

const MARKETS = [
  { id: 'h2h', label: 'Moneyline (H2H)' },
  { id: 'spreads', label: 'Spreads' },
  { id: 'totals', label: 'Totals (O/U)' },
  { id: 'outrights', label: 'Outrights' },
]

const BOOKMAKER_GROUPS = [
  { label: 'US', books: US_BOOKMAKERS },
  { label: 'UK', books: UK_BOOKMAKERS },
  { label: 'EU', books: EU_BOOKMAKERS },
  { label: 'AU', books: AU_BOOKMAKERS },
]

function OddsComparisonWidget({ initialSport, onQuotaUpdate }) {
  const [sports, setSports] = useState([])
  const [selectedSport, setSelectedSport] = useState(initialSport || '')
  const [selectedRegions, setSelectedRegions] = useState(new Set(['us']))
  const [selectedMarkets, setSelectedMarkets] = useState(new Set(['h2h']))
  const [selectedBookmakers, setSelectedBookmakers] = useState(new Set())
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [eventIds, setEventIds] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [oddsFormat, setOddsFormat] = useState('american')
  const [includeLinks, setIncludeLinks] = useState(false)
  const [includeSids, setIncludeSids] = useState(false)
  const [includeBetLimits, setIncludeBetLimits] = useState(false)
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(false)
  const [sportsLoading, setSportsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(true)
  const [lastFetch, setLastFetch] = useState(null)
  const [remaining, setRemaining] = useState(null)
  const [showCharts, setShowCharts] = useState(false)

  useEffect(() => {
    async function loadSports() {
      setSportsLoading(true)
      const res = await fetchOddsSports()
      if (res.status === 'ok' && res.data) {
        setSports(res.data.filter(s => s.active))
      }
      setSportsLoading(false)
    }
    loadSports()
  }, [])

  // Update sport if initialSport changes (from quick-odds action)
  useEffect(() => {
    if (initialSport) setSelectedSport(initialSport)
  }, [initialSport])

  const toggleRegion = (id) => {
    setSelectedRegions(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleMarket = (id) => {
    setSelectedMarkets(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleBookmaker = (key) => {
    setSelectedBookmakers(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleBookmakerGroup = (books) => {
    setSelectedBookmakers(prev => {
      const next = new Set(prev)
      const allSelected = books.every(b => next.has(b))
      books.forEach(b => allSelected ? next.delete(b) : next.add(b))
      return next
    })
  }

  // Token cost calculation
  const tokenCost = useMemo(() => {
    if (selectedBookmakers.size > 0) {
      const regionEquiv = Math.ceil(selectedBookmakers.size / 10)
      return regionEquiv * selectedMarkets.size
    }
    return selectedRegions.size * selectedMarkets.size
  }, [selectedRegions, selectedMarkets, selectedBookmakers])

  const fetchData = useCallback(async () => {
    if (!selectedSport) return
    if (selectedMarkets.size === 0) return
    if (selectedRegions.size === 0 && selectedBookmakers.size === 0) return
    if (selectedBookmakers.size > 40) return

    setLoading(true)
    setError(null)

    const params = {
      markets: Array.from(selectedMarkets).join(','),
      oddsFormat: oddsFormat,
    }

    if (selectedBookmakers.size > 0) {
      params.bookmakers = Array.from(selectedBookmakers).join(',')
    } else {
      params.regions = Array.from(selectedRegions).join(',')
    }

    if (eventIds.trim()) params.eventIds = eventIds.trim()
    if (dateFrom) params.commenceTimeFrom = new Date(dateFrom).toISOString()
    if (dateTo) params.commenceTimeTo = new Date(dateTo).toISOString()
    if (includeLinks) params.includeLinks = true
    if (includeSids) params.includeSids = true
    if (includeBetLimits) params.includeBetLimits = true

    const res = await fetchOddsForSport(selectedSport, params)

    if (res.status === 'ok' && res.data) {
      setGames(res.data)
      setLastFetch(new Date())
      if (res.remaining) setRemaining(res.remaining)
      if (onQuotaUpdate && (res.remaining || res.used)) onQuotaUpdate(res.remaining, res.used)
    } else {
      setError(res.message || 'Failed to load odds')
      setGames([])
    }

    setLoading(false)
  }, [selectedSport, selectedRegions, selectedMarkets, selectedBookmakers, eventIds, dateFrom, dateTo, oddsFormat, includeLinks, includeSids, includeBetLimits])

  const resetFilters = () => {
    setSelectedRegions(new Set(['us']))
    setSelectedMarkets(new Set(['h2h']))
    setSelectedBookmakers(new Set())
    setEventIds('')
    setDateFrom('')
    setDateTo('')
  }

  return (
    <div className="widget-card">
      <div
        className="widget-header ocw-header-clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <BarChart3 size={16} className="widget-icon" />
        <span className="widget-title">Odds Comparison</span>
        {games.length > 0 && (
          <span className="ocw-game-count">{games.length} games</span>
        )}
        <span className={`status-badge ${tokenCost > 5 ? 'warning' : 'ok'}`} style={{ fontSize: 9 }}>
          {tokenCost} TOKEN{tokenCost !== 1 ? 'S' : ''}
        </span>
        <ChevronRight
          size={16}
          className={`ocw-chevron ${expanded ? 'rotated' : ''}`}
        />
      </div>

      {expanded && (
        <>
          {/* LAY explanation info box */}
          <div className="ocw-lay-info">
            <strong>{'\u{1F4CA}'} LAY Odds Behavior:</strong>
            <ul>
              <li>LAY odds appear in the SAME team's row</li>
              <li>LAY = Betting AGAINST that team winning</li>
              <li>Exchange bookmakers marked with {'\u{1F4CA}'} indicator</li>
            </ul>
          </div>

          {/* Main controls */}
          <div className="ocw-controls">
            {/* Sport selector + odds format toggle */}
            <div className="ocw-row">
              <select
                className="ocw-select ocw-sport-select"
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                disabled={sportsLoading}
              >
                <option value="">{sportsLoading ? 'Loading...' : 'Select a sport'}</option>
                <option value="upcoming">{'\u{1F3C6}'} Upcoming (All Sports)</option>
                {sports.map(s => (
                  <option key={s.key} value={s.key}>
                    {getSportIcon(s.key)} {s.title}
                  </option>
                ))}
              </select>

              <div className="ocw-format-toggle">
                <button
                  className={`ocw-format-btn ${oddsFormat === 'american' ? 'active' : ''}`}
                  onClick={() => setOddsFormat('american')}
                >
                  American
                </button>
                <button
                  className={`ocw-format-btn ${oddsFormat === 'decimal' ? 'active' : ''}`}
                  onClick={() => setOddsFormat('decimal')}
                >
                  Decimal
                </button>
              </div>
            </div>

            {/* Regions */}
            <div className="ocw-row">
              <div className="ocw-checkbox-group">
                <span className="ocw-group-label">Regions:</span>
                {REGIONS.map(r => (
                  <label key={r.id} className={`ocw-chip ${selectedRegions.has(r.id) ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedRegions.has(r.id)}
                      onChange={() => toggleRegion(r.id)}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Markets */}
            <div className="ocw-row">
              <div className="ocw-checkbox-group">
                <span className="ocw-group-label">Markets:</span>
                {MARKETS.map(m => (
                  <label key={m.id} className={`ocw-chip ${selectedMarkets.has(m.id) ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedMarkets.has(m.id)}
                      onChange={() => toggleMarket(m.id)}
                    />
                    <span>{m.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Advanced toggle */}
            <div className="ocw-row">
              <button
                className="ocw-advanced-toggle"
                onClick={() => setShowAdvanced(v => !v)}
              >
                <Settings size={13} />
                <span>Advanced Options</span>
                <ChevronRight size={12} className={showAdvanced ? 'rotated' : ''} />
              </button>

              {/* Cost indicator */}
              <div className="ocw-cost">
                <span className={`ocw-cost-value ${tokenCost > 5 ? 'ocw-cost-high' : ''}`}>
                  {tokenCost}
                </span>
                <span className="ocw-cost-label">tokens</span>
              </div>
            </div>
          </div>

          {/* Advanced options */}
          {showAdvanced && (
            <div className="ocw-advanced">
              <div className="ocw-row">
                <input
                  type="text"
                  className="ocw-input"
                  placeholder="Event IDs (comma-separated, optional)"
                  value={eventIds}
                  onChange={(e) => setEventIds(e.target.value)}
                />
              </div>
              <div className="ocw-row">
                <div className="ocw-date-group">
                  <label className="ocw-date-label">From:</label>
                  <input
                    type="datetime-local"
                    className="ocw-date-input"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="ocw-date-group">
                  <label className="ocw-date-label">To:</label>
                  <input
                    type="datetime-local"
                    className="ocw-date-input"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>

              {/* Bookmaker selector */}
              <div className="ocw-bookmakers">
                <div className="ocw-bookmakers-header">
                  <span className="ocw-group-label">
                    Specific Bookmakers ({selectedBookmakers.size} selected)
                  </span>
                  <button
                    className="ocw-small-btn"
                    onClick={() => setSelectedBookmakers(new Set())}
                  >
                    Clear
                  </button>
                </div>
                {selectedBookmakers.size > 0 && (
                  <div className="ocw-bookmaker-note">
                    <AlertTriangle size={11} />
                    Using specific bookmakers overrides region selection.
                  </div>
                )}
                <div className="ocw-bookmaker-groups">
                  {BOOKMAKER_GROUPS.map(group => (
                    <div key={group.label} className="ocw-bookmaker-group">
                      <button
                        className="ocw-bookmaker-group-btn"
                        onClick={() => toggleBookmakerGroup(group.books)}
                      >
                        {group.label} ({group.books.filter(b => selectedBookmakers.has(b)).length}/{group.books.length})
                      </button>
                      <div className="ocw-bookmaker-list">
                        {group.books.map(b => (
                          <label key={b} className="ocw-bookmaker-check">
                            <input
                              type="checkbox"
                              checked={selectedBookmakers.has(b)}
                              onChange={() => toggleBookmaker(b)}
                            />
                            <span>{formatBookmakerName(b)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional API options */}
              <div className="ocw-extra-options">
                <span className="ocw-group-label">Include in response:</span>
                <label className="ocw-chip">
                  <input
                    type="checkbox"
                    checked={includeLinks}
                    onChange={(e) => setIncludeLinks(e.target.checked)}
                  />
                  <span>Bookmaker links</span>
                </label>
                <label className="ocw-chip">
                  <input
                    type="checkbox"
                    checked={includeSids}
                    onChange={(e) => setIncludeSids(e.target.checked)}
                  />
                  <span>Source IDs</span>
                </label>
                <label className="ocw-chip">
                  <input
                    type="checkbox"
                    checked={includeBetLimits}
                    onChange={(e) => setIncludeBetLimits(e.target.checked)}
                  />
                  <span>Bet limits</span>
                </label>
              </div>
            </div>
          )}

          {/* Action bar */}
          <div className="ocw-action-bar">
            {selectedBookmakers.size > 40 && (
              <span className="ocw-warn">Max 40 bookmakers (you have {selectedBookmakers.size})</span>
            )}
            <button
              className="ocw-fetch-btn"
              onClick={fetchData}
              disabled={
                !selectedSport ||
                selectedMarkets.size === 0 ||
                (selectedRegions.size === 0 && selectedBookmakers.size === 0) ||
                selectedBookmakers.size > 40 ||
                loading
              }
            >
              {loading ? <RefreshCw size={14} className="spinning" /> : <BarChart3 size={14} />}
              <span>{loading ? 'Loading...' : 'Get Odds'}</span>
            </button>
            <button className="ocw-reset-btn" onClick={resetFilters}>
              Reset
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="widget-body">
              <div className="widget-error">{error}</div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="widget-body">
              <div className="widget-empty">Loading odds...</div>
            </div>
          )}

          {/* Chart toggle + odds table */}
          {!loading && games.length > 0 && (
            <>
              <div className="ocw-view-toggle">
                <button
                  className={`ocw-view-btn ${!showCharts ? 'active' : ''}`}
                  onClick={() => setShowCharts(false)}
                >
                  Table
                </button>
                <button
                  className={`ocw-view-btn ${showCharts ? 'active' : ''}`}
                  onClick={() => setShowCharts(true)}
                >
                  Charts
                </button>
              </div>

              {!showCharts && <OddsTable games={games} oddsFormat={oddsFormat} />}

              {showCharts && (
                <div className="ocw-charts">
                  {games.map((game, gi) => {
                    const marketKeys = game.bookmakers
                      ? [...new Set(game.bookmakers.flatMap(b => b.markets.map(m => m.key.replace('_lay', ''))))]
                      : []
                    return marketKeys.map(mk => (
                      <OddsChart key={`${game.id || gi}-${mk}`} game={game} marketKey={mk} />
                    ))
                  })}
                </div>
              )}
            </>
          )}

          {/* Empty */}
          {!loading && games.length === 0 && lastFetch && !error && (
            <div className="widget-body">
              <div className="widget-empty">No games with odds found for this sport.</div>
            </div>
          )}

          {!loading && !lastFetch && (
            <div className="widget-body">
              <div className="widget-empty">
                Select a sport, regions, and markets, then click Get Odds.
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="widget-footer">
            <span>{lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : 'Not loaded'}</span>
            <span>
              {remaining !== null && `API remaining: ${remaining}`}
              {remaining === null && `Cost: ${tokenCost} token${tokenCost !== 1 ? 's' : ''}`}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

export default OddsComparisonWidget
